import React, { useEffect } from "react";
import {
  DialogTitle,
  IconButton,
  Select,
  MenuItem,
  Typography,
  Box,
  Tooltip,
  Button,
  Avatar,
  Stack,
} from "@mui/material";
import luxor from "../../../assets/luxor.png";
import ChatIcon from "@mui/icons-material/Chat";
import RateReviewIcon from "@mui/icons-material/RateReview";
import LockIcon from "@mui/icons-material/Lock";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDiscussionCaseContext } from "@/contexts/DiscussionCaseContext";
import { useMedicalReportsAgentContext } from "@/contexts/MedicalReportsAgentContext";
import { useGuidanceAgentContext } from "@/contexts/GuidanceAgentContext";
import { useMedicalCareContext } from "@/contexts/MedicalCareContext";
import { generateMedicalTextAndCopy } from "@/utils/medicalTextBuilder";
import {
  useFilteredConditions,
  useFilteredAllergies,
  useFilteredImmunizations,
  useFilteredSelectedInterventions,
} from "@/pages/medicalcare/components/RegisterArea/useFilteredMedicalData";
import { toast } from "react-toastify";
import { useFormatter } from "@/utils/useFormatter";
import { LuxorHeaderProps } from "../types";
import { contractTypeMapping, isTypeAvailable } from "../isTypeAvailable";
import { hasAnyActiveContract } from "../isTypeAvailable";

export const LuxorHeader: React.FC<LuxorHeaderProps> = ({
  selectedType,
  endpointConfigs,
  onTypeChange,
  onDelete,
  onClose,
  onToggleEditor,
  showEditor,
  hasResponse,
  hasHistory,
  content,
  onCopyToEditor,
}) => {
  // Contextos necessários
  const { contractDiscussionCase } = useDiscussionCaseContext();
  const { contractMedicalReports } = useMedicalReportsAgentContext();
  const { contractGuidance } = useGuidanceAgentContext();
  const {
    patient,
    problems,
    condutas,
    setCopyMedicalData,
    handleSubmit,
    registerAreaTab,
    activeStep,
  } = useMedicalCareContext();
  const { formatGender, formatAge } = useFormatter;

  // Hooks para informações médicas filtradas
  const filteredConditions = useFilteredConditions();
  const filteredAllergies = useFilteredAllergies();
  const filteredImmunizations = useFilteredImmunizations();
  const textoInterventions = useFilteredSelectedInterventions();

  // Estado local para controlar atualizações
  const [lastUpdate, setLastUpdate] = React.useState<number>(Date.now());

  useEffect(() => {
    // Força atualização quando muda de aba ou etapa
    setLastUpdate(Date.now());
  }, [registerAreaTab, activeStep, problems, condutas]);

  // Função para obter informações do paciente
  const obterInformacoesPaciente = () => {
    // Extrair informações de escolaridade do paciente
    const educacaoExtension = patient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-EducationLevel"
    );

    const escolaridade =
      educacaoExtension?.valueCodeableConcept?.coding?.[0]?.display || "-";

    return {
      idade: patient?.birthDate
        ? `${formatAge(`${patient?.birthDate}`)} anos`
        : "-",
      sexo: formatGender(patient?.gender) || "-",
      escolaridade: escolaridade,
    };
  };

  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    if (handleSubmit) {
      setLastUpdate(Date.now());

      const onSubmitLatest = (data: any) => {
        const latestCondutas = condutas;
        const latestProblems = problems;

        console.log("Copying latest data", { latestProblems, latestCondutas });

        const dataComContexto = {
          ...data,
          New_Conduta: latestCondutas,
          problems: latestProblems,
        };

        generateMedicalTextAndCopy({
          data: dataComContexto,
          infosPaciente: obterInformacoesPaciente(),
          filteredConditions,
          filteredAllergies,
          filteredImmunizations,
          textoInterventions,
          setContent: (textoFinal) => {
            // Primero atualizar o contexto
            setCopyMedicalData(textoFinal);

            // Depois chamar o onCopyToEditor para atualizar o editor
            if (onCopyToEditor) {
              console.log(
                "Calling onCopyToEditor with content length:",
                textoFinal.length
              );
              onCopyToEditor();
            }
          },
          onSuccess: () =>
            toast.success("Dados de atendimento copiados com sucesso!"),
          onError: () => toast.error("Erro ao copiar dados!"),
        });
      };

      handleSubmit(onSubmitLatest)();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else if (onCopyToEditor) {
      onCopyToEditor();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <DialogTitle
      sx={{
        border: "1px solid",
        borderColor: "primary.main",
        color: "white",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1,
        px: 2,
        marginBottom: 1,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar src={luxor} alt="Luxor" sx={{ width: 36, height: 36 }} />
        <Typography
          variant="h6"
          fontWeight={700}
          letterSpacing={0.5}
          color="primary.main"
        >
          LuxorMed
        </Typography>
      </Box>
      <Box>
        {hasResponse && !showEditor && (
          <Button
            startIcon={<ChatIcon />}
            size="small"
            variant="outlined"
            onClick={onToggleEditor}
            sx={{
              fontSize: "0.75rem",
              textTransform: "none",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "rgba(0, 174, 154, 0.1)",
              },
            }}
          >
            Editar mensagem
          </Button>
        )}

        {showEditor && (
          <Box display="flex" alignItems="center" gap={1}>
            {hasResponse && (
              <Button
                startIcon={<RateReviewIcon />}
                size="small"
                variant="outlined"
                onClick={onToggleEditor}
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary.main",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(0, 174, 154, 0.1)",
                  },
                }}
              >
                Resposta
              </Button>
            )}
            <Tooltip
              title={copied ? "Copiado!" : "Copiar atendimento"}
              placement="bottom"
            >
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{ color: "primary.main" }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <Select
          value={selectedType}
          onChange={onTypeChange}
          size="small"
          variant="outlined"
          disabled={
            !hasAnyActiveContract(
              contractDiscussionCase,
              contractMedicalReports,
              contractGuidance
            )
          }
          sx={{
            bgcolor: "whitesmoke",
            minWidth: 180,
            borderRadius: 2,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
            "& .MuiSelect-select": {
              py: 1,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&:hover": {
              bgcolor: "grey.50",
            },
          }}
          inputProps={{ "aria-label": "Tipo de análise" }}
        >
          {Object.entries(endpointConfigs).map(([key, config]) => {
            const isAvailable = isTypeAvailable(
              key,
              contractDiscussionCase,
              contractMedicalReports,
              contractGuidance
            );
            const contractType = contractTypeMapping[key];
            let tooltipText = config.description || config.name;
            if (!isAvailable) {
              if (contractType === "discussion") {
                tooltipText = "Contrato de Discussão de Casos não ativo";
              } else if (contractType === "medical-reports") {
                tooltipText = "Contrato de Relatório Médico não ativo";
              } else if (contractType === "guidance") {
                tooltipText = "Contrato de Orientação ao Paciente não ativo";
              }
            }
            return (
              <MenuItem key={key} value={key}>
                <Tooltip title={tooltipText} placement="right">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    width="100%"
                  >
                    <Typography flex={1}>{config.name}</Typography>
                    {!isAvailable && (
                      <LockIcon fontSize="small" color="disabled" />
                    )}
                  </Stack>
                </Tooltip>
              </MenuItem>
            );
          })}
        </Select>
      </Box>
    </DialogTitle>
  );
};
